import { useEffect } from 'react';
import { useWizard } from '../../../context/WizardContext';
import { NavigationButtons } from '../../../components/wizard/NavigationButtons';
import { DEPARTMENTS, requiresDepartment } from '../../../types/wizard';
import type { Department } from '../../../types/wizard';
import { cn } from '../../../lib/utils';
import { Check } from 'lucide-react';

const COLOR_MAP: Record<
  string,
  {
    selectedBg: string;
    selectedRing: string;
    selectedText: string;
    gradient: string;
  }
> = {
  blue: {
    selectedBg: 'bg-blue-50',
    selectedRing: 'ring-blue-300',
    selectedText: 'text-blue-700',
    gradient: 'from-blue-400 to-blue-600',
  },
  emerald: {
    selectedBg: 'bg-emerald-50',
    selectedRing: 'ring-emerald-300',
    selectedText: 'text-emerald-700',
    gradient: 'from-emerald-400 to-emerald-600',
  },
  violet: {
    selectedBg: 'bg-violet-50',
    selectedRing: 'ring-violet-300',
    selectedText: 'text-violet-700',
    gradient: 'from-violet-400 to-violet-600',
  },
};

export function Step1Department() {
  const { state, setDepartment, goNext } = useWizard();
  const needsDept = requiresDepartment(state.selectedClass);

  useEffect(() => {
    if (!needsDept) goNext();
  }, [needsDept, goNext]);

  if (!needsDept) {
    return (
      <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
        Skipping department selection…
      </div>
    );
  }

  const classLabel = (() => {
    if (!state.selectedClass) return '';
    return state.selectedClass <= 10 ? 'SSC (Secondary)' : 'HSC (Higher Secondary)';
  })();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Select Department</h2>
        <p className="text-sm text-slate-500 mt-1">
          Choose your department for Class {state.selectedClass} — {classLabel}.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {DEPARTMENTS.map((dept) => {
          const colors = COLOR_MAP[dept.color];
          const isSelected = state.selectedDepartment === dept.id;
          return (
            <button
              key={dept.id}
              type="button"
              onClick={() => setDepartment(dept.id as Department)}
              className={cn(
                'relative flex flex-col items-center gap-4 rounded-2xl border-2 p-6 transition-all duration-200 focus:outline-none group',
                isSelected
                  ? `border-transparent ${colors.selectedBg} ring-2 ${colors.selectedRing} shadow-md`
                  : 'border-slate-700 bg-slate-700/50 hover:border-slate-600 hover:shadow-card-hover hover:-translate-y-0.5'
              )}
            >
              {isSelected && (
                <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-white">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>
              )}
              <div
                className={cn(
                  'flex h-14 w-14 items-center justify-center rounded-2xl text-2xl shadow-md transition-transform duration-200 group-hover:scale-105',
                  `bg-gradient-to-br ${colors.gradient}`
                )}
              >
                {dept.icon}
              </div>
              <div className="text-center">
                <p
                  className={cn(
                    'font-bold text-base',
                    isSelected ? colors.selectedText : 'text-white'
                  )}
                >
                  {dept.name}
                </p>
                <p className="text-sm text-slate-500 mt-0.5">{dept.banglaName}</p>
              </div>
            </button>
          );
        })}
      </div>

      <NavigationButtons />
    </div>
  );
}
